import type { AdsgramController } from '@/types/adsgram'
import type { PageRoute } from '@/types'

export class Router {
  private currentRoute: PageRoute = 'home'
  private routes: PageRoute[] = [
    'home',
    'tasks',
    'referrals',
    'rewards',
    'leaderboard',
    'inventory',
    'marketplace',
    'profile',
  ]
  private readonly interstitialSessionKey = 'adsgram_interstitial_shown'
  private pageChangeCount: number
  private lastShownAt: number | null
  private adControllerPromise: Promise<AdsgramController | null> | null = null
  private interstitialShowPromise: Promise<void> | null = null

  constructor() {
    const interstitialState = this.readInterstitialState()
    this.pageChangeCount = interstitialState.pageChangeCount
    this.lastShownAt = interstitialState.lastShownAt
    this.setupNavigation()
    this.setupLeaderboardNavigation()
  }

  private setupNavigation(): void {
    document.querySelectorAll<HTMLButtonElement>('#topNav button').forEach((button) => {
      button.addEventListener('click', () => {
        const route = (button.dataset.route as PageRoute) || 'home'
        this.navigateTo(route)
      })
    })
  }

  private navigateTo(route: PageRoute): void {
    const prev = this.currentRoute
    this.currentRoute = route

    if (prev !== route) {
      this.onRouteChanged()
    }

    // Scroll to top when navigating to any page
    window.scrollTo({ top: 0, behavior: 'smooth' })

    // Analytics: route change
    import('@/services/analytics').then(({ track }) => {
      track('route_changed', { route, prev_route: prev })
    })

    // Update page visibility + active nav button
    this.routes.forEach((page) => {
      const pageEl = document.getElementById(`page-${page}`)
      const navBtn = document.querySelector<HTMLButtonElement>(
        `#topNav button[data-route='${page}']`
      )
      pageEl?.classList.toggle('active', page === route)
      navBtn?.classList.toggle('active', page === route)
    })

    // Show/hide main nav based on route
    const topNav = document.getElementById('topNav')
    if (topNav) {
      const hideTopNav = route === 'leaderboard' || route === 'marketplace' || route === 'profile'
      topNav.style.display = hideTopNav ? 'none' : 'flex'
    }

    // Page-specific hooks
    switch (route) {
      case 'rewards':
        window.dispatchEvent(new Event('rewardsPageShown'))
        break
      case 'referrals':
        window.dispatchEvent(new Event('referralPageShown'))
        break
      case 'tasks':
        window.dispatchEvent(new Event('tasksPageShown'))
        break
      case 'leaderboard':
        window.dispatchEvent(new Event('leaderboardPageShown'))
        break
      case 'profile':
        window.dispatchEvent(new Event('profilePageShown'))
        break
      case 'inventory':
        window.dispatchEvent(new Event('inventoryPageShown'))
        break
      case 'marketplace':
        window.dispatchEvent(new Event('marketplacePageShown'))
        break
      // home: nothing special
    }
  }



  private setupLeaderboardNavigation(): void {
    // Trophy button to open leaderboard
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement
      if (target.closest('#trophyBtn')) {
        this.navigateTo('leaderboard')
      }
      if (target.closest('#marketBtn')) {
        this.navigateTo('marketplace')
      }
    })

    // Back button in leaderboard to return to home
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement
      if (target.closest('#leaderboardBack')) {
        this.navigateTo('home')
      }
      if (target.closest('#marketplaceBack')) {
        this.navigateTo('home')
      }
      if (target.closest('#profileBack')) {
        this.navigateTo('leaderboard')
      }
    })
  }

  public goTo(route: PageRoute): void {
    this.navigateTo(route)
  }

  getCurrentRoute(): PageRoute {
    return this.currentRoute
  }

  private onRouteChanged(): void {
    this.pageChangeCount += 1
    this.persistInterstitialState()

    if (!this.shouldShowInterstitial()) {
      return
    }

    if (this.interstitialShowPromise) {
      return
    }

    const changeCount = this.pageChangeCount

    this.interstitialShowPromise = this.showInterstitial(changeCount).finally(() => {
      this.interstitialShowPromise = null
    })
  }

  private shouldShowInterstitial(): boolean {
    if (this.pageChangeCount === 0) {
      return false
    }

    if (this.lastShownAt === this.pageChangeCount) {
      return false
    }

    if (this.pageChangeCount === 1) {
      return true
    }

    return this.pageChangeCount % 10 === 0
  }

  private async showInterstitial(changeCount: number): Promise<void> {
    const controller = await this.getAdController()
    if (!controller) {
      return
    }

    try {
      await controller.show()
    } catch (error) {
      console.warn('Adsgram interstitial failed', error)
    } finally {
      this.updateInterstitialState(changeCount)
    }
  }

  private getAdController(): Promise<AdsgramController | null> {
    if (this.adControllerPromise) {
      return this.adControllerPromise
    }

    const controllerPromise = new Promise<AdsgramController | null>((resolve) => {
      const initialize = (): AdsgramController | null => {
        if (!window.Adsgram?.init) {
          return null
        }

        try {
          return window.Adsgram.init({ blockId: 'int-17657' })
        } catch (error) {
          console.error('Adsgram init failed', error)
          return null
        }
      }

      const controller = initialize()
      if (controller) {
        resolve(controller)
        return
      }

      let attempts = 0
      const maxAttempts = 20
      const interval = window.setInterval(() => {
        attempts += 1
        const pendingController = initialize()
        if (pendingController) {
          window.clearInterval(interval)
          resolve(pendingController)
        } else if (attempts >= maxAttempts) {
          window.clearInterval(interval)
          resolve(null)
        }
      }, 200)
    })

    this.adControllerPromise = controllerPromise.then((controller) => {
      if (!controller) {
        this.adControllerPromise = null
      }
      return controller
    })

    return this.adControllerPromise
  }

  private readInterstitialState(): { pageChangeCount: number; lastShownAt: number | null } {
    try {
      const rawState = sessionStorage.getItem(this.interstitialSessionKey)
      if (!rawState) {
        return { pageChangeCount: 0, lastShownAt: null }
      }

      const parsedState = JSON.parse(rawState)

      if (typeof parsedState === 'number') {
        const count = Number.isFinite(parsedState)
          ? Math.max(0, Math.floor(parsedState))
          : 0
        const lastShown = count > 0 ? count : null
        return { pageChangeCount: count, lastShownAt: lastShown }
      }

      if (typeof parsedState === 'object' && parsedState !== null) {
        const count =
          typeof parsedState.pageChangeCount === 'number' &&
          Number.isFinite(parsedState.pageChangeCount)
            ? Math.max(0, Math.floor(parsedState.pageChangeCount))
            : 0
        const lastShown =
          typeof parsedState.lastShownAt === 'number' &&
          Number.isFinite(parsedState.lastShownAt)
            ? Math.max(0, Math.floor(parsedState.lastShownAt))
            : null
        return { pageChangeCount: count, lastShownAt: lastShown }
      }

      return { pageChangeCount: 0, lastShownAt: null }
    } catch (error) {
      console.warn('Unable to access sessionStorage for Adsgram interstitial state', error)
      return { pageChangeCount: 0, lastShownAt: null }
    }
  }

  private persistInterstitialState(): void {
    try {
      sessionStorage.setItem(
        this.interstitialSessionKey,
        JSON.stringify({
          pageChangeCount: this.pageChangeCount,
          lastShownAt: this.lastShownAt,
        })
      )
    } catch (error) {
      console.warn('Unable to persist Adsgram interstitial state', error)
    }
  }

  private updateInterstitialState(changeCount: number): void {
    this.lastShownAt = changeCount
    this.persistInterstitialState()
  }
}
